import React, { type ComponentProps, useCallback, useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Tabs } from 'expo-router';
import { LabelText, MainTabBarV2, theme, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { Navbar } from '../../components';
import { useHasUnseenChallenges } from '../../hooks';
import { APP_BACKGROUND, useTabOptions } from '../../routing';

// ─────────────────────────────────────────────────────────────────────────────

export default function TabsLayout() {
  const { currentBreakpoint, minWidth } = useResponsive();
  const hasUnseenChallenges = useHasUnseenChallenges();
  const isDemoBadgeEnabled = useFeatureFlag('CELS-4877-order-expo-router-demo-badge-enabled'); // prettier-ignore

  const {
    homeTabOptions,
    menuTabOptions,
    giftTabOptions,
    loyaltyTabOptions,
    reorderTabOptions,
  } = useTabOptions();

  const tabsDisplayingBadge = hasUnseenChallenges ? LOYALTY_TABS_WITH_BADGE : EMPTY; // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  const screenOptions = useMemo<TabsScreenOptions>(
    () => ({
      headerShown: !currentBreakpoint.isXS,
      header: currentBreakpoint.isXS ? undefined : renderNavbar,
      sceneContainerStyle: { backgroundColor: APP_BACKGROUND },
      tabBarStyle: {
        backgroundColor: APP_BACKGROUND,
        borderTopWidth: 1,
        borderTopColor: theme.colors.OPACITY.DARK_KALE.DARK,
        paddingTop: theme.spacing[2],
      },
      tabBarActiveTintColor: theme.colors.KALE,
      tabBarInactiveTintColor: theme.colors.OPACITY.BLACK.DARK,
    }),
    [currentBreakpoint.isXS],
  );

  // ─── Tab Bar ────────────────────────────────────────────────────────

  const renderTabBar = useCallback<TabBarRenderer>(
    (props) => {
      if (minWidth.isSM) return null;

      return (
        <View>
          {isDemoBadgeEnabled ? (
            <View pointerEvents="none" style={styles.expoRouterBadge}>
              <LabelText sizeMatch={['12']}>Expo Router</LabelText>
            </View>
          ) : null}

          <MainTabBarV2 {...props} tabsDisplayingBadge={tabsDisplayingBadge} />
        </View>
      );
    },
    [isDemoBadgeEnabled, minWidth.isSM, tabsDisplayingBadge],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Tabs
      screenOptions={screenOptions}
      tabBar={renderTabBar}
      backBehavior={Platform.select({ web: 'history', default: 'firstRoute' })}
    >
      <Tabs.Screen name="(home)" options={homeTabOptions} />
      <Tabs.Screen name="(menu)" options={menuTabOptions} />
      <Tabs.Screen name="sg-rewards" options={loyaltyTabOptions} />
      <Tabs.Screen name="reorder-tab" options={reorderTabOptions} />
      <Tabs.Screen name="gift" options={giftTabOptions} />

      {/* hide some tabs */}

      <Tabs.Screen name="account" options={{ href: null }} />
      <Tabs.Screen name="menu" options={{ href: null }} />
    </Tabs>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function renderNavbar() {
  return <Navbar />;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  expoRouterBadge: {
    position: 'absolute',
    top: 0,
    left: '50%',
    zIndex: 1,
    transform: [{ translateX: '-50%' }, { translateY: '-100%' }],
    backgroundColor: theme.colors.LIME,
    borderTopLeftRadius: theme.spacing['2'],
    borderTopRightRadius: theme.spacing['2'],
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['2'],
    ...theme.elevations['3'],
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const LOYALTY_TABS_WITH_BADGE = ['sg-rewards'] as const;
const EMPTY = [] as const;

// ─── Types ──────────────────────────────────────────────────────────────────

type TabsScreenOptions = NonNullable<
  ComponentProps<typeof Tabs>['screenOptions']
>;

type TabBarRenderer = NonNullable<ComponentProps<typeof Tabs>['tabBar']>;
